import { 
  RotateCw, 
  ChevronRight,X,ArrowUpRight
} from 'lucide-react';
import { Subscription } from '../../types/api';
import { SubscriptionState } from '../../types';
import { useTranslation } from 'react-i18next';
import { FEATURES } from '../../config';

interface SubscriptionActionsProps {
  subscription: Subscription | SubscriptionState;
  onPause: () => void;
  onResume: () => void;
  onCancel: () => void;
  onUpgrade?: () => void;
  onRenew?: () => void;
}

const SubscriptionActions: React.FC<SubscriptionActionsProps> = ({
  subscription,
  onUpgrade,
  onRenew,
  onCancel
}) => {
  const { t } = useTranslation();
  
  // Determine the effective status to display
  const getEffectiveStatus = () => {
    // Check if subscription should show as pending renewal
    if (subscription.status === 'active' && 
        subscription.isRecurring === false && 
        subscription.remainingDeliveries === 0) {
      return 'pending_renewal';
    }
    return subscription.status;
  };

  const effectiveStatus = getEffectiveStatus();
  
  if (effectiveStatus === 'cancelled') {
    return null;
  }

  // Calculate if renewal button should be highlighted based on remaining deliveries or pending renewal
  const shouldHighlightRenewal = effectiveStatus === 'pending_renewal' || 
    (subscription.status === 'active' && 
    subscription.isRecurring === false && 
    subscription.remainingDeliveries !== undefined && 
    subscription.remainingDeliveries <= 2);
  
  return (
    <div className="bg-white p-0">
      {/* <h3 className="text-lg font-semibold mb-4">Subscription Actions</h3> */}
      <div className="space-y-1">
        {/* Show upgrade button for standard subscriptions */}
        {subscription.plan === 'standard' && subscription.status === 'active' && (
          <div 
            onClick={onUpgrade}
            className='px-4 py-3 flex items-center cursor-pointer hover:bg-amber-50'
          >
            <div className='w-10 h-10 bg-amber-200 rounded-full flex items-center justify-center mr-4'>
              <ArrowUpRight className='w-6 h-6 text-amber-500' />
            </div>
            <div className='flex flex-col flex-1'>
              <span className='font-medium text-base'>Upgrade to Premium</span>
              <span className='text-xs text-gray-400'>Get access to more vases and premium features</span>
            </div>
            <ChevronRight className='w-5 h-5 text-amber-400' />
          </div>
        )}
        
        {(effectiveStatus === 'active' || effectiveStatus === 'pending_renewal') && subscription.isRecurring !== true && (
          <div 
            onClick={FEATURES.ENABLE_RENEWALS ? onRenew : undefined}
            className={`px-4 py-3 flex items-center ${FEATURES.ENABLE_RENEWALS ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'} ${shouldHighlightRenewal ? 'bg-amber-50 hover:bg-amber-100' : 'hover:bg-amber-50'}`}
          >
            <div className={`w-10 h-10 ${shouldHighlightRenewal ? 'bg-amber-200' : 'bg-amber-100'} rounded-full flex items-center justify-center mr-4`}>
              <RotateCw className={`w-6 h-6 ${shouldHighlightRenewal ? 'text-amber-600' : 'text-amber-500'}`} />
            </div>
            <div className='flex flex-col flex-1'>
              <span className={`font-medium text-base ${shouldHighlightRenewal ? 'text-amber-700' : ''}`}>
                {FEATURES.ENABLE_RENEWALS ? t('subscription.actions.renewSubscription') : t('common.serviceUnavailable')}
              </span>
              <span className='text-xs text-gray-400'>
                {effectiveStatus === 'pending_renewal'
                  ? t('subscription.actions.renewSoonMessage')
                  : shouldHighlightRenewal 
                    ? subscription.remainingDeliveries === 1 
                      ? t('subscription.actions.renewSoonMessage') 
                      : t('subscription.actions.renewLowMessage')
                    : t('subscription.actions.renewDescription')}
              </span>
              {shouldHighlightRenewal && (
                <span className='text-xs font-semibold text-amber-600 mt-1'>
                  {effectiveStatus === 'pending_renewal'
                    ? t('subscription.actions.deliveriesRemaining', { count: 0 })
                    : subscription.remainingDeliveries !== undefined
                      ? t('subscription.actions.deliveriesRemaining', { count: subscription.remainingDeliveries })
                      : ''}
                </span>
              )}
            </div>
            <ChevronRight className={`w-5 h-5 ${shouldHighlightRenewal ? 'text-amber-500' : 'text-gray-400'}`} />
          </div>
        )}
        
        {(effectiveStatus === 'active' || effectiveStatus === 'pending_renewal' || effectiveStatus === 'paused') && (
          <div 
            onClick={onCancel}
            className='px-4 py-3 flex items-center cursor-pointer hover:bg-red-50'
          >
            <div className='w-10 h-10 bg-red-100 rounded-full flex items-center justify-center mr-4'>
              <X className='w-6 h-6 text-red-500' />
            </div>
            <div className='flex flex-col flex-1'>
              <span className='font-medium text-base'>{t('subscription.actions.cancelSubscription')}</span>
              <span className='text-xs text-gray-400'>{t('subscription.actions.cancelDescription')}</span>
            </div>
            <ChevronRight className='w-5 h-5 text-gray-400' />
          </div>
        )}
      </div>
    </div>
  );
};

export default SubscriptionActions; 